import {
  NODE_ENVS,
  IGNORE_CSV_LABEL,
  DISPLAY_BREAKPOINTS,
  UNDER_SCORE,
} from "@/constants";
import capitalize from "lodash/capitalize";

/**
 * Calculates the date from which the accounts record are to be fetched
 * @param {Number} days User selected days
 * @returns {Object} from_date is current date minus days param and to_date is current date
 */
export const getSelectedFilterDayDate = (days) => {
  const date = new Date();
  const [to_date] = date.toISOString().split("T");

  const [from_date] = new Date(date.setUTCDate(date.getUTCDate() - days))
    .toISOString()
    .split("T");

  return {
    from_date,
    to_date,
  };
};

/**
 * Checks if the object is empty or not
 * @param {Object,Array} object Object to check if it is empty or not
 * @returns {Boolean} Boolean value determining if object is empty or not
 */
export const isEmpty = (object) => {
  if (Array.isArray(object)) {
    return object && object.length === 0;
  } else if (object && isType(object, "object") && object instanceof Object) {
    return Object.keys(object).length === 0 && object.constructor === Object;
  } else {
    return true;
  }
};

/**
 * Checks if the object is of a particular data type
 * @param {*} object Object to check it's data type
 * @param {*} type Data type of the object to be expected
 * @returns Boolean Whether it of specified data type
 */
export const isType = (object, type) => typeof object === type;

/**
 * Checks the api response code with the status param of the api response
 * @todo Add new api response code cases in the function
 * @param {Number} code Api code to check
 * @param {Number} status Api status to be validated with
 * @returns {Boolean} Is api status same as
 */
export const isResponseCode = (code, status) => code === status;

/**
 * Avoid user to enter "e" or "E" char in number fields
 * @param {Object} event
 */
export const onlyNumber = (event) => {
  let keyCode = event.keyCode ? event.keyCode : event.which;
  if ((keyCode < 48 || keyCode > 57) && keyCode !== 46) {
    event.preventDefault();
  }
};

/**
 * Determines if NODE_ENV env is test
 * @returns {Boolean} Boolean value determining that it is test env or not
 */
export const isTestEnv = () => process.env.NODE_ENV === NODE_ENVS.test;

/**
 * Delays execution of a function
 * @param {Function} callback Function to be executed after milliseconds delay
 * @param {String} timeout Millieseconds after which the callback function will be executed
 */
export const defer = (callback, timeout) => {
  const ERROR = "defer callback argument should be a function.";

  if (!isType(callback, "function")) throw new Error(ERROR);
  return setTimeout(callback, timeout);
};

/**
 * Computes the csv file labels
 * @param {Array} headers Array of csv file labels
 * @returns {Object} Object containing labels of csv file to be downloaded
 */
export const csvFileLabels = (headers) => {
  const ERROR = "csvFileLabels method headers argument should be an array.";
  if (!Array.isArray(headers)) throw new Error(ERROR);

  let labels = {};
  // Checks if label is ignored or not
  const isLabelIgnored = (value) => value === IGNORE_CSV_LABEL;

  headers?.forEach(
    ({ text, value }) => !isLabelIgnored(value) && (labels[value] = text)
  );
  return labels;
};

/**
 * Utitlity function to remove a item in an array
 * @param {Array} item Item to be removed
 * @param {Array} items Items from which item has to be removed
 */
export const removeArrayItem = (item, itemProp, items) => {
  // Finds the item in items array
  const index = items.findIndex((record) => {
    const identifier = itemProp ? record[itemProp] : record;
    return identifier === item;
  });
  // Removed the item if it is present in array of items
  if (index !== -1) items.splice(index, 1);
};

/**
 * Iterates over the array of object and retuns a new array which contains all prop value in the array objects
 * @param {Array} arr Array of object to be iterated
 * @param {String} prop Property name whose value are to be fetched from the array
 * @returns {Array} Array of all the prop values in it
 */

export const getPropArr = (arr, prop) => {
  if (!Array.isArray(arr) || !prop) return [];

  const propsArr = arr.map((data) => data[prop]);
  return propsArr;
};

/**
 * Updates the store value
 * @param {Object} oldState Old state of the store
 * @param {Object} updatedState Updated state of the store
 * @returns {Object} Updated state
 */
export const updateStore = (oldState, updatedState) => ({
  ...oldState,
  ...updatedState,
});

/**
 * Creates a new object containing all key value pair properties present in the props array
 * @param {Object} object Object from which the properties are to be fetched
 * @param {Array} props Array of string (Object properties) name which are to be fetched from object argument and then added into new object
 * @returns {Object} New object containing properties
 */
export const pick = (object, props) => {
  let newObject = {};

  // Iterates over props array and add key value pair in newObject
  props.forEach((prop) => {
    // Checks whether property exixts in exixting object
    const hasProp = Object.hasOwnProperty.call(object, prop);
    if (hasProp) newObject[prop] = object[prop];
  });

  return newObject;
};

export const uniqByProp = (data, uniqProp, isObject = false) => {
  const uniqObj = data.reduce(
    (map, obj) => map.set(obj[uniqProp], obj),
    new Map()
  );

  return isObject ? [...uniqObj.values()] : [...uniqObj.keys()];
};

/**
 * Fetches logged in user machine timezone
 * @description If the current browser of the user Intl api then it shows if not then the timezone is gonna be utc
 */
export const getUserTimezone = () => {
  return Intl.DateTimeFormat()?.resolvedOptions()?.timeZone ?? "";
};

/**
 * Determines that current view is mobile or not
 * @returns {Boolean}
 */
export const isMobile = ({ breakpoint }) => {
  const { sm, xs } = DISPLAY_BREAKPOINTS;
  return [sm, xs].includes(breakpoint.name);
};

/**
 * Replaces {{name}} word value with dynamic value passed in the param
 * @param {String} value Text value to be replaced in textString
 * @returns {String} Updated text string with dynamic value
 */
export const nameReplacer = (textString, value) => {
  if (!textString || !value) return;

  return textString.replace("{{name}}", value);
};

/**
 * Computes name of a user
 * @returns {String}
 */
export const getName = ({ first_name, last_name }) => {
  return `${capitalize(first_name) ?? ""} ${capitalize(last_name) ?? ""}`;
};

/**
 * Function to check if an object contains an property
 * @param {Object} object Object in which to find a property is defined or not
 * @param {String} prop Name of the property to check its presence in the Object recieved in the param
 * @returns {Boolean} Is property present in the object or not
 */
export const hasProp = (object, prop) => {
  if (!object || !prop)
    throw new Error(
      "hasProp function has object and prop as required arguments."
    );
  return Object.prototype.hasOwnProperty.call(object, prop);
};

/**
 * Utitlity function to replace underscore characters in a string
 * @param {String} value String value
 * @returns {String} Updated string with underscore characters removed with single space
 */
export const removeUnderscore = (value) => {
  if (!value)
    throw new Error("removeUnderscore function requires value arguments.");
  return value.includes(UNDER_SCORE)
    ? value.replaceAll(UNDER_SCORE, " ")
    : value;
};
